import {AfterViewInit, Component, OnDestroy, OnInit, TemplateRef, ViewChild} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CounsellorsMomService } from '../counsellors-mom.service';
import { CounsellorsMom } from '../counsellors-mom.model';
import { AddMomCounsellorComponent } from './add-mom-counsellor/add-mom-counsellor.component';
import {FormControl} from "@angular/forms";
import {PermissionType} from "../../../../../components/employee/PermissionType.enum";
import {LogService} from "../../../../../services/log.service";
import {Subject} from "rxjs";
import {takeUntil} from "rxjs/operators";

/**
 * Component for listing and managing Counsellor MOM sessions
 */
@Component({
  selector: 'app-mom-counsellors',
  templateUrl: './mom-counsellors.component.html',
  styleUrls: ['./mom-counsellors.component.scss']
})
export class MomCounsellorsComponent implements OnInit ,AfterViewInit, OnDestroy{
  private _destroyed$ = new Subject<void>();

  displayedColumns: string[] = ['date', 'participants', 'actions'];
  dataSource = new MatTableDataSource<CounsellorsMom>([]);
  isLoading = false;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  dateFilter = new FormControl('');
  allSessions: CounsellorsMom[] = [];
  currentUserName: string = '';
  @ViewChild('viewDialog') viewDialog!: TemplateRef<any>;
  selectedSession: CounsellorsMom | null = null;

  constructor(
    private counsellorsMomService: CounsellorsMomService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private mLogService: LogService,
  ) { }

  ngOnInit(): void {
    this.loadSessions();

    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    this.currentUserName = userData.userName || '';
  }
  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }


  /**
   * Load all counsellor MOM sessions
   */
  loadSessions() {
    this.isLoading = true;
    this.counsellorsMomService.getAll().pipe(takeUntil(this._destroyed$)).subscribe(
      sessions => {
        this.allSessions = sessions.sort((a, b) => {
          const dateA = a.date?.toDate ? a.date.toDate() : new Date(a.date);
          const dateB = b.date?.toDate ? b.date.toDate() : new Date(b.date);
          return dateB.getTime() - dateA.getTime();
        });
        console.log(this.allSessions);

        this.dataSource.data = [...this.allSessions];
        this.isLoading = false;
      }
    );
  }
  openViewDialog(session: CounsellorsMom) {
    this.selectedSession = session;

    this.dialog.open(this.viewDialog, {
      width: '900px',
      disableClose: true
    });
  }

  closeView() {
    this.dialog.closeAll();
  }


  canEditSession(session: CounsellorsMom): boolean {
    // Always show for Krisha Shah and Vaishali Nanda
    if (this.currentUserName === 'Krisha Shah' ||this.currentUserName === 'Master Admin' || this.currentUserName === 'Vaishali Nanda') {
      return true;
    }

    // Check if within 4 days of the session date
    const sessionDate = session.date?.toDate ? session.date.toDate() : new Date(session.date);
    const currentDate = new Date();

    // Set time to midnight for accurate day comparison
    sessionDate.setHours(0, 0, 0, 0);
    currentDate.setHours(0, 0, 0, 0);

    // Calculate difference in days
    const diffTime = currentDate.getTime() - sessionDate.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    // Show edit button if within 4 days (0-4 days inclusive)
    return diffDays >= 0 && diffDays <= 4;
  }
  getDate(val: any): Date | null {
    if (!val) return null;

    // Firestore timestamp object
    if (val.seconds) {
      return new Date(val.seconds * 1000);
    }

    // Firestore Timestamp instance
    if (val.toDate) {
      return val.toDate();
    }

    return new Date(val);
  }


  onSearch() {
    const selectedDate = this.dateFilter.value;

    if (!selectedDate) {
      this.snackBar.open('Please select a date to filter', 'Close', {
        duration: 2000,
        horizontalPosition: 'center',
        verticalPosition: 'top'
      });
      return;
    }

    const filterDate = new Date(selectedDate);
    filterDate.setHours(0, 0, 0, 0);

    const filteredSessions = this.allSessions.filter(session => {
      const sessionDate = session.date?.toDate ? session.date.toDate() : new Date(session.date);
      sessionDate.setHours(0, 0, 0, 0);
      return sessionDate.getTime() === filterDate.getTime();
    });

    this.dataSource.data = filteredSessions;

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }

    if (filteredSessions.length === 0) {
      this.snackBar.open('No sessions found for the selected date', 'Close', {
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'top'
      });
    }
  }

  onClear() {
    this.dateFilter.setValue('');
    this.dataSource.data = [...this.allSessions];

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }

    this.snackBar.open('Filters cleared', 'Close', {
      duration: 2000,
      horizontalPosition: 'center',
      verticalPosition: 'top'
    });
  }

  /**
   * Open dialog to add new session
   */
  openAddDialog() {
    const dialogRef = this.dialog.open(AddMomCounsellorComponent, {
      width: '95%',
      maxWidth: '1400px',
      data: null
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'saved' || result === 'updated') {
        this.loadSessions();
      }
    });
  }

  /**
   * Open dialog to edit existing session
   */
  openEditDialog(session: CounsellorsMom) {
    const dialogRef = this.dialog.open(AddMomCounsellorComponent, {
      width: '95%',
      maxWidth: '1400px',
      data: session
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'saved' || result === 'updated') {
        this.loadSessions();
      }
    });
  }

  /**
   * Delete a session
   */
  deleteSession(session: CounsellorsMom) {
    if (confirm('Are you sure you want to delete this session?')) {
      this.counsellorsMomService.deleteRow(session.id!).then(() => {
        this.snackBar.open('Session deleted successfully', 'Close', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['success-snackbar']
        });
        const activity = {
          date: new Date().getTime(),
          section: 'Counsellors MOM',
          action: 'Deleted',
          description: `Counsellors MOM details deleted by user`,
          currentIp: localStorage.getItem('currentip')!
        };
        this.mLogService.addLog(activity);
        this.loadSessions();
      }).catch((error: any) => {
        this.snackBar.open('Error deleting session: ' + error.message, 'Close', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        });
      });
    }
  }

  /**
   * Apply filter to the table
   */
  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  /**
   * Format date for display
   */
  formatDate(date: any): string {
    if (!date) return '';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString();
  }

  /**
   * Get participant names as comma-separated string
   */
  getParticipantNames(participants: string[]): string {
    if (!participants || participants.length === 0) return '';
    return participants.join(', ');
  }

  /**
   * Get count of session items
   */
  getSessionCount(session: CounsellorsMom): number {
    return session.sessions?.length || 0;
  }

  protected readonly PermissionType = PermissionType;

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }
}


